import { ApiError } from 'api/generated';
import { formatErrorMessage, QueryError } from 'features/shared/model';
import { useMutation, UseMutationOptions } from 'react-query';
import { toast } from 'react-toastify';

export type MutationOptionsWithoutFn<
  TData = unknown,
  TError = QueryError,
  TVariables = void,
  TContext = unknown
> = Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'>;

export interface NotificationOptions<TError> {
  successMessage?: string;
  errorMessage?: string | ((error: TError) => string);
  enableToast?: boolean;
}

export function useMutationWithNotifications<
  TData = unknown,
  TError = QueryError,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: MutationOptionsWithoutFn<TData, TError, TVariables, TContext>,
  notificationsOptions?: NotificationOptions<TError>
) {
  const {
    successMessage = 'Operation completed successfully!',
    errorMessage = formatErrorMessage,
    enableToast = false
  } = notificationsOptions || {};

  return useMutation(mutationFn, {
    onSuccess: (data, variables, context) => {
      if (enableToast) {
        toast.success(successMessage);
      }
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      const formattedMessage =
        typeof errorMessage === 'function' ? errorMessage(error as ApiError & TError) : errorMessage;
      if (enableToast) {
        toast.error(formattedMessage);
      }
      console.error(formattedMessage);
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options
  });
}

export type ApiMutationOptions<
  TFn extends (...args: any) => Promise<any>,
  TVariables = Parameters<TFn>[0],
  TContext = unknown
> = MutationOptionsWithoutFn<Awaited<ReturnType<TFn>>, QueryError, TVariables, TContext>;
